import React, { useEffect, useState, useMemo } from 'react';
import { CreditApplicationRequestForm } from '../CreditApplicationRequestForm/CreditApplicationRequestForm';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  getCreditApplicationTemplate,
  getCreditApplicationTemplates,
} from '../../../../redux/services/creditApplicationsTemplatesService';
import {
  CreditApplicationTemplate,
  getCreditApplicationTypeLabel,
  isFormTemplateType,
  toCreditApplicationType,
} from '@dill/dill-shared';
import { getBasicSupplier } from '../../../../redux/services/buyersService';
import dillLogoWithText from '../../../../assets/svg/dill_logo_with_text.svg';
import { ShieldCheck, ClockClockwise, Headset, IdentificationCard } from 'phosphor-react';

const BADGE_ICON_STYLE = { background: 'linear-gradient(135deg, #3762FB 0%, #1D85FF 100%)' };

const TrustBadge = ({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) => (
  <div className="flex items-start gap-3 p-3 rounded-xl WHITE-BG shadow-sm border GREY_100-BORDER">
    <div
      className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-full"
      style={BADGE_ICON_STYLE}>
      {icon}
    </div>
    <div>
      <p className="text-sm font-semibold GREY_900-CLR">{title}</p>
      <p className="text-xs GREY_500-CLR mt-0.5">{description}</p>
    </div>
  </div>
);

const SupportCard = () => (
  <div className="p-4 rounded-xl WHITE-BG shadow-sm border GREY_100-BORDER">
    <div className="flex items-center gap-2 mb-3">
      <div
        className="flex-shrink-0 flex items-center justify-center w-9 h-9 rounded-full"
        style={BADGE_ICON_STYLE}>
        <Headset size={18} className="text-white" weight="fill" />
      </div>
      <p className="text-sm font-semibold GREY_900-CLR">Need Help?</p>
    </div>
    <div className="space-y-1">
      <a
        href="mailto:support@usedill.com"
        className="flex items-center gap-1.5 text-xs hover:opacity-80 transition-opacity">
        <span className="GREY_500-CLR">Email:</span>
        <span className="PRIMARY_500-CLR font-medium">support@usedill.com</span>
      </a>
      <a
        href="tel:6283040931"
        className="flex items-center gap-1.5 text-xs hover:opacity-80 transition-opacity">
        <span className="GREY_500-CLR">Phone:</span>
        <span className="PRIMARY_500-CLR font-medium">(628) 304-0931</span>
      </a>
    </div>
  </div>
);

const SidebarSkeleton = () => (
  <div className="animate-pulse flex flex-col gap-4">
    <div className="w-full h-24 rounded-2xl GREY_200-BG" />
    <div className="flex flex-col items-center gap-2">
      <div className="h-4 w-32 rounded GREY_200-BG" />
      <div className="h-3 w-24 rounded GREY_200-BG" />
      <div className="h-3 w-20 rounded GREY_200-BG" />
    </div>
    {[1, 2, 3].map((i) => (
      <div
        key={i}
        className="flex items-start gap-3 p-3 rounded-xl GREY_100-BG border GREY_100-BORDER">
        <div className="w-9 h-9 rounded-full GREY_200-BG flex-shrink-0" />
        <div className="flex flex-col gap-1.5 flex-1">
          <div className="h-3 w-16 rounded GREY_200-BG" />
          <div className="h-2.5 w-full rounded GREY_200-BG" />
        </div>
      </div>
    ))}
    <div className="p-4 rounded-xl GREY_100-BG border GREY_100-BORDER flex flex-col gap-2">
      <div className="h-3 w-20 rounded GREY_200-BG" />
      <div className="h-2.5 w-36 rounded GREY_200-BG" />
      <div className="h-2.5 w-28 rounded GREY_200-BG" />
    </div>
  </div>
);

const ContentSkeleton = () => (
  <div className="animate-pulse flex flex-col gap-6 px-5 md:px-10 py-6 flex-1">
    <div className="h-6 w-48 rounded GREY_200-BG" />
    <div className="flex flex-col gap-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="flex flex-col gap-2">
          <div className="h-3 w-24 rounded GREY_200-BG" />
          <div className="h-10 w-full rounded-lg GREY_200-BG" />
        </div>
      ))}
    </div>
  </div>
);

export const CreditApplicationRequest = () => {
  const dispatch = useAppDispatch();
  const [selectedCreditApplicationTemplate, setSelectedCreditApplicationTemplate] =
    useState<CreditApplicationTemplate | null>(null);
  const [domainDetails, setDomainDetails] = useState<{
    id: string;
    domainId: string;
    url: string;
    name: string;
    branches: { name: string; erpId?: string }[];
    salesReps: { name: string; erpId?: string }[];
  } | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<CreditApplicationTemplate | null>(null);
  const [availableTemplates, setAvailableTemplates] = useState<CreditApplicationTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [supplierNotFound, setSupplierNotFound] = useState(false);

  const { creditApplicationTemplate } = useAppSelector((state) => state.creditApplicationTemplates);
  const { creditApplicationTemplates } = useAppSelector(
    (state) => state.creditApplicationTemplates
  );
  const { basicSupplier } = useAppSelector((state) => state.buyers);

  useEffect(() => {
    if (basicSupplier) {
      const branches = (basicSupplier?.branches ?? [])
        .filter((branch) => branch?.notInCreditApp !== true)
        .map((branch) => ({ name: branch?.branchName ?? '', erpId: branch?.erpId ?? '' }));
      const seenSalesReps = new Set<string>();
      const salesReps = (basicSupplier?.branches ?? [])
        .filter((branch) => branch?.notInCreditApp !== true)
        .flatMap((branch) => branch?.salesRep ?? [])
        .filter((rep) => {
          const name = rep?.name ?? '';
          if (!name || seenSalesReps.has(name)) return false;
          seenSalesReps.add(name);
          return true;
        })
        .map((rep) => ({ name: rep?.name ?? '', erpId: rep?.erpId ?? '' }));
      setDomainDetails({
        url: basicSupplier?.logo?.url ?? '',
        name: basicSupplier?.name ?? '',
        id: basicSupplier?.id,
        domainId: basicSupplier?.domainId,
        branches,
        salesReps,
      });
      setIsLoading(false);
    }
  }, [basicSupplier]);

  useEffect(() => {
    // An internal form's templateId in a credit application link is ignored — those are only
    // filled through /fillInternalForm. External forms are filled here like any application.
    if (
      creditApplicationTemplate &&
      !isFormTemplateType(creditApplicationTemplate.applicationType)
    ) {
      setSelectedCreditApplicationTemplate(creditApplicationTemplate);
      setSelectedTemplate(creditApplicationTemplate);
    }
  }, [creditApplicationTemplate]);

  useEffect(() => {
    if (basicSupplier?.id) {
      dispatch(getCreditApplicationTemplates({ supplierId: basicSupplier.id }));
    }
  }, [basicSupplier?.id, dispatch]);

  useEffect(() => {
    // The picker offers credit / COD applications only; an external form opens from its own link.
    const applicationTemplates = (creditApplicationTemplates ?? []).filter(
      (t) => t.applicationType === 'CREDIT_APPLICATION' || t.applicationType === 'CASH_APPLICATION'
    );
    if (applicationTemplates.length > 0) {
      setAvailableTemplates(applicationTemplates);
      const params = new URLSearchParams(location.search);
      if (!params.get('templateId')) {
        const creditAppTemplate = applicationTemplates.find(
          (t) => t.applicationType === 'CREDIT_APPLICATION'
        );
        if (creditAppTemplate) {
          setSelectedTemplate(creditAppTemplate);
          setSelectedCreditApplicationTemplate(creditAppTemplate);
        }
      }
    }
  }, [creditApplicationTemplates]);

  const addTemplateIdToUrl = (templateId: string) => {
    const url = new URL(window.location.href);
    url.searchParams.set('templateId', templateId);
    window.history.replaceState({}, '', url.toString());
  };

  const handleTemplateSelect = (template: CreditApplicationTemplate) => {
    setSelectedTemplate(template);
    setSelectedCreditApplicationTemplate(template);
    addTemplateIdToUrl(template.id);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paramTemplateId = params.get('templateId');
    if (paramTemplateId) {
      dispatch(getCreditApplicationTemplate({ templateId: paramTemplateId }));
    }
    const paramDomainId = params.get('domainId');
    if (paramDomainId) {
      setIsLoading(true);
      dispatch(getBasicSupplier({ supplierDomainId: paramDomainId })).then((result) => {
        if (!getBasicSupplier.fulfilled.match(result) || !result.payload?.data) {
          setIsLoading(false);
          setSupplierNotFound(true);
        }
      });
    } else {
      setSupplierNotFound(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const title = useMemo(() => {
    if (selectedTemplate) {
      return getCreditApplicationTypeLabel(
        toCreditApplicationType(selectedTemplate.applicationType),
        {
          long: true,
          formName: selectedTemplate.name,
        }
      );
    }
    return ' Application';
  }, [selectedTemplate]);

  const address = `${basicSupplier?.address ? `${basicSupplier?.address},` : ''}`;

  const cityStateZip = `${basicSupplier?.city ? `${basicSupplier?.city},` : ''} ${
    basicSupplier?.state ?? ''
  } ${basicSupplier?.zipCode ?? ''}`;

  return (
    <div className="w-full GREY_100-BG min-h-screen flex flex-col md:flex-row">
      {/* Sidebar */}
      <div className="flex flex-col md:justify-between px-5 py-8 w-full md:w-[260px] GREY_50-BG border-b md:border-b-0 md:border-r GREY_200-BORDER md:h-screen md:sticky md:top-0 md:overflow-y-auto">
        {isLoading ? (
          <SidebarSkeleton />
        ) : (
          <>
            {/* Supplier branding */}
            <div className="flex flex-col items-center gap-3 mb-6">
              {domainDetails?.url ? (
                <div className="flex items-center justify-center w-full h-24 p-3 rounded-2xl WHITE-BG shadow-sm border GREY_100-BORDER">
                  <img
                    src={domainDetails.url}
                    className="max-h-16 max-w-full object-contain"
                    alt={domainDetails.name}
                  />
                </div>
              ) : (
                <div className="flex items-center justify-center w-full h-20 p-4 rounded-2xl WHITE-BG shadow-sm border GREY_100-BORDER">
                  <img className="w-7/12 object-contain" src={dillLogoWithText} alt="Dill" />
                </div>
              )}
              <div className="text-center">
                <p className="text-base font-bold GREY_900-CLR">{basicSupplier?.name ?? ''}</p>
                {address && <p className="text-xs GREY_500-CLR mt-0.5">{address}</p>}
                {cityStateZip.trim() && <p className="text-xs GREY_500-CLR">{cityStateZip}</p>}

                <div className="flex flex-col items-center mt-0.5">
                  {(basicSupplier?.creditApplicationContactEmail?.length
                    ? basicSupplier.creditApplicationContactEmail.join(', ')
                    : basicSupplier?.email) && (
                    <a
                      href={`mailto:${
                        basicSupplier?.creditApplicationContactEmail?.[0] ?? basicSupplier?.email
                      }`}
                      className="text-xs PRIMARY_500-CLR hover:opacity-80 transition-opacity">
                      {basicSupplier?.creditApplicationContactEmail?.join(', ') ??
                        basicSupplier?.email}
                    </a>
                  )}
                  {(basicSupplier?.creditApplicationContactPhoneNumber?.length
                    ? basicSupplier.creditApplicationContactPhoneNumber.join(', ')
                    : basicSupplier?.phoneNumber) && (
                    <a
                      href={`tel:${
                        basicSupplier?.creditApplicationContactPhoneNumber?.[0] ??
                        basicSupplier?.phoneNumber
                      }`}
                      className="text-xs PRIMARY_500-CLR hover:opacity-80 transition-opacity">
                      {basicSupplier?.creditApplicationContactPhoneNumber?.join(', ') ??
                        basicSupplier?.phoneNumber}
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* Trust badges */}
            <div className="flex flex-col gap-3 mb-6">
              <TrustBadge
                icon={<ShieldCheck size={18} className="text-white" weight="fill" />}
                title="Secure"
                description="Your data is fully encrypted and protected."
              />
              <TrustBadge
                icon={<ClockClockwise size={18} className="text-white" weight="fill" />}
                title="Quick & Easy"
                description="Complete your application in minutes."
              />
              <TrustBadge
                icon={<IdentificationCard size={18} className="text-white" weight="fill" />}
                title="No Account Required"
                description="Apply without creating an account."
              />
            </div>

            {/* Support */}
            <SupportCard />
          </>
        )}
      </div>

      {/* Main content */}
      <div className="flex flex-col flex-1 min-w-0">
        {/* Header */}
        <div className="flex items-center justify-between px-6 md:px-10 py-4 WHITE-BG border-b GREY_200-BORDER shadow-sm">
          {isLoading ? (
            <div className="animate-pulse h-6 w-48 rounded GREY_200-BG" />
          ) : (
            <h1 className="text-lg md:text-xl font-bold GREY_900-CLR truncate mr-4">
              {selectedTemplate?.name
                ? selectedTemplate.name
                : domainDetails?.name
                ? `${domainDetails.name} ${title}`
                : title}
            </h1>
          )}
          <a
            href="https://usedill.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 flex items-center gap-1.5 hover:opacity-80 transition-opacity">
            <span className="text-xs GREY_500-CLR whitespace-nowrap">Powered by</span>
            <img className="h-7 md:h-9" src={dillLogoWithText} alt="Dill" />
          </a>
        </div>

        {/* Body */}
        {isLoading && <ContentSkeleton />}

        {!isLoading &&
          !supplierNotFound &&
          !selectedCreditApplicationTemplate &&
          availableTemplates.length === 0 && (
            <div className="flex-1 flex items-center justify-center px-6 py-16">
              <div className="flex flex-col items-center gap-6 text-center max-w-sm w-full">
                <div
                  className="flex items-center justify-center w-16 h-16 rounded-2xl shadow-md"
                  style={BADGE_ICON_STYLE}>
                  <IdentificationCard size={32} className="text-white" weight="fill" />
                </div>
                <div className="flex flex-col gap-2">
                  <h2 className="text-lg font-bold GREY_900-CLR">No Application Found</h2>
                  <p className="text-sm GREY_500-CLR leading-relaxed">
                    We couldn't find a credit or COD application template for this link. This may be
                    a temporary issue or the link may be incorrect.
                  </p>
                </div>
                <div className="w-full p-4 rounded-xl WHITE-BG shadow-sm border GREY_100-BORDER text-left">
                  <p className="text-xs font-semibold GREY_700-CLR mb-2">Contact Support</p>
                  <div className="flex flex-col gap-1.5">
                    <a
                      href="mailto:support@usedill.com"
                      className="flex items-center gap-1.5 text-xs hover:opacity-80 transition-opacity">
                      <span className="GREY_500-CLR">Email:</span>
                      <span className="PRIMARY_500-CLR font-medium">support@usedill.com</span>
                    </a>
                    <a
                      href="tel:6283040931"
                      className="flex items-center gap-1.5 text-xs hover:opacity-80 transition-opacity">
                      <span className="GREY_500-CLR">Phone:</span>
                      <span className="PRIMARY_500-CLR font-medium">(628) 304-0931</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}

        {supplierNotFound && (
          <div className="flex-1 flex items-center justify-center px-6 py-16">
            <div className="flex flex-col items-center gap-6 text-center max-w-sm w-full">
              <div
                className="flex items-center justify-center w-16 h-16 rounded-2xl shadow-md"
                style={BADGE_ICON_STYLE}>
                <IdentificationCard size={32} className="text-white" weight="fill" />
              </div>
              <div className="flex flex-col gap-2">
                <h2 className="text-lg font-bold GREY_900-CLR">Invalid Link</h2>
                <p className="text-sm GREY_500-CLR leading-relaxed">
                  This application link is invalid or has expired. Please check the URL or contact
                  the supplier who sent it to you.
                </p>
              </div>
              <div className="w-full p-4 rounded-xl WHITE-BG shadow-sm border GREY_100-BORDER text-left">
                <p className="text-xs font-semibold GREY_700-CLR mb-2">Contact Support</p>
                <div className="flex flex-col gap-1.5">
                  <a
                    href="mailto:support@usedill.com"
                    className="flex items-center gap-1.5 text-xs hover:opacity-80 transition-opacity">
                    <span className="GREY_500-CLR">Email:</span>
                    <span className="PRIMARY_500-CLR font-medium">support@usedill.com</span>
                  </a>
                  <a
                    href="tel:6283040931"
                    className="flex items-center gap-1.5 text-xs hover:opacity-80 transition-opacity">
                    <span className="GREY_500-CLR">Phone:</span>
                    <span className="PRIMARY_500-CLR font-medium">(628) 304-0931</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}

        {!isLoading && selectedCreditApplicationTemplate && (
          <div className="flex-1 px-5 md:px-10 py-6 overflow-y-auto">
            <CreditApplicationRequestForm
              domainDetails={domainDetails}
              availableTemplates={availableTemplates}
              selectedTemplate={selectedTemplate}
              onSelectTemplate={handleTemplateSelect}
            />
          </div>
        )}
      </div>
    </div>
  );
};
